/**
 * SkillCard — the skill **Trainer card** on the shared `TradingCard` frame
 * (BL-60 ⭐, ART §9 + §6.8). The owner's mandate: *"die Skill-Trainer-Cards
 * müssen 1000% werden — ein MEGA-CATCH im Spiel!"*
 *
 * BL-59 shipped the reusable `TradingCard` frame (enemy reveal = a Pokémon card).
 * BL-60 brings the skills onto that SAME frame as **Trainer cards** (`kind:
 * "skill"`, a compact card aspect) — Supporter/Item/Tool/Stadium → our
 * Offensive/Defensive/Utility/Legendary, *the ability/tool itself is the hero*
 * (no AI-trainer figure). This class is now a thin **skill→`TradingCard`
 * adapter**: it maps a `Card` to a `TradingCardContent`, builds the shared frame,
 * and mounts the real living `SkillEmblem` / `LegendaryEmblem` hero via
 * `TradingCard.mountHolo`. The card's own frame draw is GONE — the frame is
 * `TradingCard` now; the value-add is the emblem hero + the **catch
 * choreography**.
 *
 * THE CATCH (the headline). The draft is a **pack pull**: the card arrives
 * **sealed** (an encrypted intercept back with a rarity **telegraph** charge),
 * then **flips/decrypts** into the Trainer face with a rarity-scaled flourish
 * (`playPull`). The clean reveal (`playReveal`, the `[S]` collection / swap)
 * skips the seal. Both feed `TradingCard.playReveal` (the slam + name decode +
 * the rarity frame flare / legendary gold-foil loop) + the emblem construct.
 * `LegendaryEmblem` (WebGL-3D gold) is the apex; the modal-level legendary
 * detonation (the gold wash / vignette) is orchestrated by `DraftUI`.
 *
 * Public API preserved for the surfaces: `constructor(card, opts)` +
 * `playReveal` / `select` / `dismiss` / `.el`. Motion is `isReducedMotion()`-
 * gated everywhere: reduced → instant-but-rich (no seal/flip/charge/flash).
 */
import type { Card } from "../entities/types.ts";
import { getPersistenceTag, type PersistenceTag } from "./cardPersistence.ts";
import { rarityMaterial, type RarityName } from "../core/palette.ts";
import { roleFontSize, withUiScale } from "./uiTokens.ts";
import {
  TradingCard,
  type TradingCardContent,
  type TradingCardRarity,
} from "./TradingCard.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { SkillEmblem, emblemArchetypeForCard } from "./SkillEmblem.ts";
import { LegendaryEmblem } from "./LegendaryEmblem.ts";
import { localizedName } from "../utils/i18n.ts";

const RARITY_SHORT: Record<RarityName, string> = {
  common: "[C]",
  rare: "[R]",
  epic: "[E]",
  legendary: "[L]",
};

/** card rarity → the shared frame's foil tier (common reads as BASIC). */
const RARITY_TIER: Record<RarityName, TradingCardRarity> = {
  common: "basic",
  rare: "rare",
  epic: "epic",
  legendary: "legendary",
};

/** Rarity pips — the apex jumps 3→5 so a legendary reads as the chase card. */
const RARITY_PIPS: Record<RarityName, number> = {
  common: 1,
  rare: 2,
  epic: 3,
  legendary: 5,
};

/** Category → the Trainer "type" badge symbol + the codename role line + the
 *  dossier prefix. The ability/tool is the hero — these read as the card's TYPE. */
const TYPE_BADGE: Record<string, { symbol: string; role: string; tag: string }> =
  {
    offensive: { symbol: "◆", role: "STRIKE PROTOCOL", tag: "OFF" },
    defensive: { symbol: "◇", role: "BASTION PROTOCOL", tag: "DEF" },
    utility: { symbol: "⬡", role: "SUPPORT PROTOCOL", tag: "UTL" },
    legendary: { symbol: "★", role: "CLASS-Ω PROTOCOL", tag: "LEG" },
  };

/** Phase tempo for the mega-catch pull (ms). The legendary charges longer (the
 *  tension build) — `DraftUI` reads these to sync the legendary detonation. */
export const PULL_TIMING = {
  deal: 170,
  chargeBase: 360,
  chargeLegendaryExtra: 320,
  flip: 520,
} as const;

export interface SkillCardOptions {
  /** `draft` = the pull / swap card (the full hero, lively emblem); `overview` =
   *  the compact `[S]` gallery card (a static hero — the dense-gallery discipline). */
  variant: "draft" | "overview";
  /** Localised display name (caller resolves i18n; falls back to `card.name`). */
  name?: string;
  /** Localised description (caller resolves i18n). */
  description?: string;
  /** Extra root classes (e.g. `draft-card` for the draft selection/fly logic). */
  extraClasses?: string[];
  /** A factual swap cue rendered as a corner tag (e.g. `↓ LOWER`, `LOWEST`). */
  cue?: string;
  /** The cue's factual tone — `lowest` (weakest held), `up` (held outranks the
   *  incoming), `down` (held is lower than the incoming). */
  cueTone?: "lowest" | "up" | "down";
  /** BL-296 — max drawn emblem radius (CSS px) for a host with a genuinely large
   *  art window. Only the draft row sets it; every compact surface omits it and
   *  keeps the default small-hero cap. The art window still clips. */
  heroRadius?: number;
}

export class SkillCard {
  /** The card root — the flip wrapper (perspective + lift), crisp DOM inside. */
  readonly el: HTMLDivElement;
  /** The shared rarity-tiered frame (the front face). */
  private readonly card: TradingCard;
  private readonly flip: HTMLDivElement;
  private readonly front: HTMLDivElement;
  private seal: HTMLDivElement | null = null;
  private readonly rarity: RarityName;
  private readonly material: { mid: string; intensity: number; bloomWeight: number };
  private readonly variant: "draft" | "overview";
  /** BL-296 — the host's emblem-radius cap; undefined = the default small hero. */
  private readonly heroRadius: number | undefined;
  /** The living emblem (standard tiers + the legendary D′ enriched-canvas rung). */
  private emblem: SkillEmblem | null = null;
  /** The legendary WebGL-3D apex (rung D); null when not legendary or no WebGL2. */
  private legendary: LegendaryEmblem | null = null;
  /** Which legendary rung mounted — surfaced for the WOW sign-off readout. */
  emblemRung: "webgl" | "enriched-canvas" | "canvas" = "canvas";
  private selected = false;
  private hovered = false;
  private destroyed = false;
  private sealed = false;
  private readonly timers: ReturnType<typeof setTimeout>[] = [];

  constructor(card: Card, opts: SkillCardOptions) {
    SkillCard.ensureStyles();
    const mat = rarityMaterial(card.rarity);
    this.rarity = (
      ["common", "rare", "epic", "legendary"].includes(card.rarity)
        ? card.rarity
        : "common"
    ) as RarityName;
    this.material = {
      mid: mat.mid,
      intensity: mat.intensity,
      bloomWeight: mat.bloomWeight,
    };
    this.variant = opts.variant;
    this.heroRadius = opts.heroRadius;

    const persistence = getPersistenceTag(card);
    const name = opts.name ?? card.name;
    const desc = opts.description ?? card.description;

    // The shared frame — a Trainer card built from the real `Card` fields.
    this.card = new TradingCard(
      this.buildContent(card, name, desc, persistence),
    );

    // The flip wrapper: the root the surfaces append + style. The TradingCard is
    // the FRONT face; the sealed encrypted back is built lazily for the pull.
    this.el = document.createElement("div");
    this.el.className = [
      "skillcard",
      `skillcard--${opts.variant}`,
      `rarity-${this.rarity}`,
      ...(opts.extraClasses ?? []),
    ].join(" ");
    this.el.style.setProperty("--rarity-mid", mat.mid);
    this.el.style.setProperty("--rarity-core", this.card.el.style.getPropertyValue("--tc-core"));
    if (persistence.kind === "payload")
      this.el.classList.add("skillcard--payload");

    this.flip = document.createElement("div");
    this.flip.className = "skillcard-flip";
    this.front = document.createElement("div");
    this.front.className = "skillcard-face skillcard-face--front";
    this.front.appendChild(this.card.el);
    this.flip.appendChild(this.front);
    this.el.appendChild(this.flip);

    // A factual swap cue (the FROZEN factual-not-prescriptive decision support).
    if (opts.cue) {
      const cue = document.createElement("div");
      cue.className = `skillcard-cue${
        opts.cueTone ? ` skillcard-cue--${opts.cueTone}` : ""
      }`;
      cue.textContent = opts.cue;
      this.el.appendChild(cue);
    }

    // The living emblem hero — mounted into the frame's art window.
    this.mountEmblem(card);

    this.el.addEventListener("mouseenter", () => this.setHover(true));
    this.el.addEventListener("mouseleave", () => this.setHover(false));
    this.attachTilt();
  }

  // ─── Data → Trainer card ─────────────────────────────────────────────────────

  /** Map a `Card` to the shared frame's content (guardrail ④). category → type
   *  badge + role + dossier; rarity → the foil tier + pips; headline/description/
   *  duration → the effect block + persistence chip. Colour stays central. */
  private buildContent(
    card: Card,
    name: string,
    desc: string,
    persistence: PersistenceTag,
  ): TradingCardContent {
    const badge = TYPE_BADGE[card.category] ?? TYPE_BADGE.utility!;
    const headline = card.headline
      ? localizedName("card", `${card.id}.headline`, card.headline)
      : "";
    return {
      kind: "skill",
      rarity: RARITY_TIER[this.rarity],
      colour: this.material.mid, // skill identity hue = its rarity (one read)
      classification: "⚡ SKILL",
      classBadge: this.rarity.toUpperCase(),
      name,
      subtitle: badge.role,
      typeBadge: {
        symbol: badge.symbol,
        label: card.category.toUpperCase(),
        cat: card.category,
      },
      effect: headline
        ? { headline, description: desc }
        : { description: desc },
      persistence: { label: persistence.label, kind: persistence.kind },
      footerLeft: `AXIOM · ${badge.tag} ${dossierNo(card.id)}`,
      pips: RARITY_PIPS[this.rarity],
      flavour: `${RARITY_SHORT[this.rarity]} intercepted · archived`,
    };
  }

  /** Mount the living emblem hero: the legendary WebGL-3D apex when available
   *  (rung D), else an enriched-canvas gold sigil (D′); standard tiers get the
   *  archetype canvas emblem. Routed through the ONE shared bloom (no per-card
   *  WebGL except the isolated legendary). */
  private mountEmblem(card: Card): void {
    if (this.rarity === "legendary") {
      // BL-296 — the WebGL apex is a fixed-size canvas, so it tracks the same
      // hero radius the procedural emblems use (diameter = 2r) and legendary
      // never renders SMALLER than a common on the same card.
      const leg = LegendaryEmblem.create(
        this.heroRadius ? Math.round(this.heroRadius * 2) : 96,
      );
      if (leg) {
        this.legendary = leg;
        this.emblemRung = "webgl";
        this.card.mountHolo(leg.el);
        return;
      }
      this.emblemRung = "enriched-canvas";
      this.emblem = new SkillEmblem({
        archetype: "legendary",
        colour: this.material.mid,
        bloomWeight: this.material.bloomWeight,
        bloom: SkillEmblem.sharedBloom(),
        intensity: this.material.intensity,
        ...(this.heroRadius === undefined ? {} : { maxRadius: this.heroRadius }),
      });
      this.card.mountHolo(this.emblem.el);
      return;
    }
    this.emblemRung = "canvas";
    this.emblem = new SkillEmblem({
      archetype: emblemArchetypeForCard(card),
      colour: this.material.mid,
      bloomWeight: this.material.bloomWeight,
      bloom: SkillEmblem.sharedBloom(),
      intensity: this.material.intensity,
      ...(this.heroRadius === undefined ? {} : { maxRadius: this.heroRadius }),
    });
    this.card.mountHolo(this.emblem.el);
  }

  // ─── Choreography (§6.8) — every step reduced-motion-gated ───────────────────

  /** The CLEAN reveal — the `[S]` collection / swap. No seal: the frame slams +
   *  decodes (the shared `TradingCard.playReveal`) and the hero constructs (draft
   *  liveliness) / draws static (the dense-gallery discipline). */
  playReveal(delayMs = 0): void {
    if (this.destroyed) return;
    if (isReducedMotion()) {
      this.revealFront();
      return;
    }
    this.schedule(() => this.revealFront(), delayMs);
  }

  /** ⭐ The MEGA-CATCH pull — sealed arrival + rarity telegraph → flip/decrypt →
   *  the Trainer face. `onFlip` fires as the flip starts (DraftUI hooks the
   *  legendary detonation there). Reduced-motion → instant-but-rich (no seal). */
  playPull(opts: { delayMs?: number; onFlip?: () => void } = {}): void {
    if (this.destroyed) return;
    const delayMs = opts.delayMs ?? 0;
    if (isReducedMotion()) {
      this.schedule(() => {
        this.revealFront();
        opts.onFlip?.();
      }, 0);
      return;
    }

    this.buildSeal();
    this.sealed = true;
    this.el.classList.add("revealed", "skillcard--sealed");
    this.el.style.setProperty("--flip", "180deg");

    // 1 — deal the sealed intercept in.
    this.schedule(() => {
      if (this.destroyed) return;
      this.el.classList.add("skillcard--dealt");
    }, delayMs);

    // 2 — the rarity telegraph (the "is one of these a holo?" charge build).
    const chargeAt = delayMs + PULL_TIMING.deal;
    this.schedule(() => {
      if (this.destroyed) return;
      this.el.classList.add("skillcard--charging");
    }, chargeAt);

    // 3 — the flip/decrypt into the Trainer face.
    const chargeMs =
      PULL_TIMING.chargeBase +
      (this.rarity === "legendary" ? PULL_TIMING.chargeLegendaryExtra : 0) +
      this.material.bloomWeight * 120;
    const flipAt = chargeAt + chargeMs;
    this.schedule(() => {
      if (this.destroyed) return;
      this.sealed = false;
      this.el.classList.remove("skillcard--charging");
      this.el.classList.add("skillcard--flipping");
      this.el.style.setProperty("--flip", "0deg");
      opts.onFlip?.();
    }, flipAt);

    // 4 — at the flip midpoint, decode the face in (the front becomes the read).
    this.schedule(() => {
      if (this.destroyed) return;
      this.el.classList.remove("skillcard--sealed");
      this.revealFront();
    }, flipAt + PULL_TIMING.flip * 0.5);
  }

  /** Reveal the front face: the shared frame slam + decode, the hero construct,
   *  the legendary flare. Idempotent + reduced-motion-safe. */
  private revealFront(): void {
    if (this.destroyed) return;
    this.el.classList.add("revealed");
    this.el.style.setProperty("--flip", "0deg");
    this.card.playReveal(); // slam + name decode + rarity frame flare / gold loop
    if (this.variant === "overview") {
      this.emblem?.drawStatic();
      this.legendary?.start();
    } else {
      this.emblem?.playConstruct(60);
      this.legendary?.start();
      if (this.rarity === "legendary") this.playLegendaryFlare();
    }
  }

  /** The legendary drop the screen acknowledges — a one-shot gold flare. */
  private playLegendaryFlare(): void {
    if (isReducedMotion()) return;
    this.el.classList.remove("skillcard--legendary-flare");
    void this.el.offsetWidth; // reflow so a replay re-triggers
    this.el.classList.add("skillcard--legendary-flare");
    this.schedule(() => {
      if (!this.destroyed)
        this.el.classList.remove("skillcard--legendary-flare");
    }, 900);
  }

  /** The chosen card commits the pull: a punch flash + the emblem power-up. */
  select(): void {
    this.selected = true;
    this.hovered = false;
    this.el.classList.add("selected");
    this.el.style.setProperty("--tilt-x", "0deg");
    this.el.style.setProperty("--tilt-y", "0deg");
    this.emblem?.powerUp();
    if (isReducedMotion()) return;
    this.el.classList.remove("skillcard--install");
    void this.el.offsetWidth;
    this.el.classList.add("skillcard--install");
  }

  /** A non-chosen sibling decays away. */
  dismiss(): void {
    this.el.classList.add("dismissed");
  }

  private setHover(on: boolean): void {
    if (this.hovered === on || this.selected || this.sealed) return;
    this.hovered = on;
    this.el.classList.toggle("hovered", on);
    this.emblem?.setHover(on);
  }

  /** Card depth: a CSS-3D tilt of the whole flip toward the cursor + an opposite
   *  parallax on the emblem (set on the frame's art var). Reduced-motion → flat.
   *  Inert while sealed (the back doesn't tilt) or selected (punches square-on). */
  private attachTilt(): void {
    if (typeof window === "undefined") return;
    const handler = (e: MouseEvent): void => {
      if (
        this.destroyed ||
        isReducedMotion() ||
        this.selected ||
        this.sealed
      )
        return;
      const r = this.el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const nx = (e.clientX - r.left) / r.width - 0.5;
      const ny = (e.clientY - r.top) / r.height - 0.5;
      this.el.style.setProperty("--tilt-x", `${(-ny * 22).toFixed(2)}deg`);
      this.el.style.setProperty("--tilt-y", `${(nx * 22).toFixed(2)}deg`);
      this.card.el.style.setProperty("--tc-parx", `${(nx * 12).toFixed(1)}px`);
      this.card.el.style.setProperty("--tc-pary", `${(ny * 12).toFixed(1)}px`);
    };
    const reset = (): void => {
      this.el.style.setProperty("--tilt-x", "0deg");
      this.el.style.setProperty("--tilt-y", "0deg");
      this.card.el.style.setProperty("--tc-parx", "0px");
      this.card.el.style.setProperty("--tc-pary", "0px");
    };
    this.el.addEventListener("mousemove", handler);
    this.el.addEventListener("mouseleave", reset);
  }

  /** Build the sealed/encrypted intercept back — a scrambled holo-foil card back
   *  with the SENTINEL sigil + a rarity-tinted telegraph charge. Lazy + cheap. */
  private buildSeal(): void {
    if (this.seal) return;
    const back = document.createElement("div");
    back.className = "skillcard-face skillcard-face--back";
    const sym = "▚▞▚ ▞▚▞ ▚▚▞";
    back.innerHTML = `
      <div class="skillcard-seal-grid" aria-hidden="true"></div>
      <div class="skillcard-seal-charge" aria-hidden="true"></div>
      <div class="skillcard-seal-stamp">⚠ ENCRYPTED INTERCEPT</div>
      <div class="skillcard-seal-sigil">◈</div>
      <div class="skillcard-seal-scramble">${sym}<br>${sym}<br>${sym}</div>
      <div class="skillcard-seal-foot">SENTINEL · DECRYPTING…</div>`;
    this.seal = back;
    this.flip.appendChild(back);
  }

  private schedule(fn: () => void, delayMs: number): void {
    if (delayMs <= 0) {
      fn();
      return;
    }
    this.timers.push(setTimeout(fn, delayMs));
  }

  destroy(): void {
    this.destroyed = true;
    for (const t of this.timers) clearTimeout(t);
    this.timers.length = 0;
    this.emblem?.destroy();
    this.emblem = null;
    this.legendary?.destroy();
    this.legendary = null;
    this.card.destroy();
    this.el.remove();
  }

  // ─── One injected stylesheet (built FROM the §9 tokens) ──────────────────────

  private static stylesInjected = false;

  static ensureStyles(): void {
    if (SkillCard.stylesInjected || typeof document === "undefined") return;
    SkillCard.stylesInjected = true;
    const style = document.createElement("style");
    style.id = "skillcard-styles";
    style.textContent = `
      /* The flip wrapper — perspective + the lift/select transform; the frame
         carries the surface, so this stays transparent (no doubled border). */
      .skillcard {
        position: relative; display: block;
        perspective: 1100px;
        opacity: 0;
        transform: translateY(var(--lift-y, 0px)) scale(var(--lift-s, 1));
        transition: transform 140ms cubic-bezier(0.215,0.61,0.355,1),
                    opacity 260ms cubic-bezier(0.16,1,0.3,1);
      }
      .skillcard.revealed { opacity: 1; }
      .skillcard.hovered { --lift-y: -6px; --lift-s: 1.03; }
      .skillcard.selected { --lift-y: -14px; --lift-s: 1.08; }
      .skillcard.dismissed {
        --lift-y: 22px; --lift-s: 0.85; opacity: 0.16; pointer-events: none;
      }

      .skillcard-flip {
        position: relative; width: 100%; height: 100%;
        transform-style: preserve-3d;
        transform: rotateY(calc(var(--flip, 0deg) + var(--tilt-y, 0deg)))
                   rotateX(var(--tilt-x, 0deg));
        transition: transform 520ms cubic-bezier(0.16,1,0.3,1);
      }
      .skillcard-face {
        position: absolute; inset: 0; width: 100%; height: 100%;
        backface-visibility: hidden; -webkit-backface-visibility: hidden;
      }
      .skillcard-face--back { transform: rotateY(180deg); }

      /* The frame fills the host box; drop the frame's own perspective() so the
         flip wrapper's perspective governs (no nested-3D fights), keep tilt. */
      .skillcard .tc--skill {
        width: 100%; height: 100%;
        backface-visibility: hidden; -webkit-backface-visibility: hidden;
        transform: none;
      }

      /* ── the factual swap cue — a high-contrast top-edge RIBBON TAB ──
         BL-60 polish (owner fix ⑤): the cue was a low-contrast corner chip that
         overlapped the SKILL/category banner. It is now a centred tab that
         protrudes just above the card's top edge — dark text on a bright tone
         fill (the factual rarity-vs-incoming read), so it is instantly legible
         and NEVER occludes the name / emblem / effect. The tone fills reuse the
         existing cue hues (no new hex); "= SAME" reads in the card's rarity hue. */
      .skillcard-cue {
        position: absolute; top: -11px; left: 50%; transform: translateX(-50%);
        z-index: 8; font-family: 'VT323','Courier New',monospace;
        font-size: ${roleFontSize("micro")}; line-height: 1; letter-spacing: 1.5px; white-space: nowrap;
        padding: 3px 10px 2px; border-radius: 4px; text-transform: uppercase;
        color: #05070c; font-weight: 700;
        background: var(--cue-fill, var(--rarity-core, #cfe));
        border: 1px solid rgba(2,4,8,0.55);
        box-shadow: 0 2px 7px rgba(0,0,0,0.55), 0 0 10px var(--cue-glow, transparent);
        pointer-events: none;
      }
      .skillcard-cue--lowest { --cue-fill: #FFB884; --cue-glow: rgba(255,164,96,0.5); }
      .skillcard-cue--up { --cue-fill: #88F2B8; --cue-glow: rgba(136,242,184,0.55); }
      .skillcard-cue--down { --cue-fill: #8AA0B8; --cue-glow: rgba(138,160,184,0.4); }

      /* ── the sealed / encrypted intercept back (the telegraph) ── */
      .skillcard-face--back {
        display: flex; flex-direction: column; align-items: center;
        justify-content: center; gap: 6px; overflow: hidden;
        border-radius: 12px; box-sizing: border-box; padding: 14px;
        background:
          radial-gradient(circle at 50% 38%,
            color-mix(in srgb, var(--rarity-mid) 22%, #05070c) 0%, #04050a 70%);
        border: 1px solid color-mix(in srgb, var(--rarity-mid) 55%, transparent);
        font-family: 'VT323','Courier New',monospace;
      }
      .skillcard-seal-grid {
        position: absolute; inset: 0; pointer-events: none; opacity: 0.5;
        background:
          repeating-linear-gradient(0deg, transparent 0 7px, color-mix(in srgb, var(--rarity-mid) 18%, transparent) 7px 8px),
          repeating-linear-gradient(90deg, transparent 0 7px, color-mix(in srgb, var(--rarity-mid) 14%, transparent) 7px 8px);
      }
      .skillcard-seal-charge {
        position: absolute; inset: 0; pointer-events: none; opacity: 0;
        background: radial-gradient(circle at 50% 42%,
          color-mix(in srgb, var(--rarity-core, #fff) 55%, transparent) 0%, transparent 60%);
        transition: opacity 240ms ease;
      }
      .skillcard-seal-stamp {
        position: relative; z-index: 1; font-size: ${roleFontSize("micro")}; letter-spacing: 2px;
        color: color-mix(in srgb, var(--rarity-core, #fff) 80%, #bcd);
      }
      .skillcard-seal-sigil {
        position: relative; z-index: 1; font-size: ${withUiScale(46)}; line-height: 1;
        color: var(--rarity-core, #cfe);
        text-shadow: 0 0 14px color-mix(in srgb, var(--rarity-mid) 70%, transparent);
      }
      .skillcard-seal-scramble {
        position: relative; z-index: 1; font-size: ${roleFontSize("label")}; letter-spacing: 3px;
        text-align: center; opacity: 0.55; color: var(--rarity-mid, #9ab);
      }
      .skillcard-seal-foot {
        position: relative; z-index: 1; font-size: ${roleFontSize("micro")}; letter-spacing: 2px;
        opacity: 0.7; color: var(--rarity-core, #cfe);
      }
      /* the deal-in + the rarity-scaled charge telegraph. */
      .skillcard--sealed { opacity: 1; }
      .skillcard--dealt .skillcard-flip { animation: skillcardDeal 320ms cubic-bezier(0.16,1,0.3,1); }
      @keyframes skillcardDeal {
        0% { transform: rotateY(180deg) translateY(-18px) scale(0.86); }
        100% { transform: rotateY(180deg) translateY(0) scale(1); }
      }
      .skillcard--charging .skillcard-seal-charge { opacity: 0.9; }
      .skillcard--charging .skillcard-face--back {
        animation: skillcardCharge 0.7s ease-in-out infinite alternate;
      }
      @keyframes skillcardCharge {
        0% { box-shadow: inset 0 0 18px color-mix(in srgb, var(--rarity-mid) 20%, transparent); }
        100% { box-shadow: inset 0 0 26px color-mix(in srgb, var(--rarity-core) 45%, transparent),
                           0 0 22px color-mix(in srgb, var(--rarity-mid) 45%, transparent); }
      }
      /* the legendary telegraph runs hotter (a hard gold pre-glow). */
      .skillcard.rarity-legendary.skillcard--charging .skillcard-face--back {
        animation-duration: 0.45s;
      }
      .skillcard.rarity-legendary.skillcard--charging .skillcard-seal-charge { opacity: 1; }

      /* ⭐ the legendary drop flare on reveal. */
      .skillcard--legendary-flare { animation: skillcardLegendaryFlare 0.9s cubic-bezier(0.16,1,0.3,1); }
      @keyframes skillcardLegendaryFlare {
        0% { filter: brightness(1); }
        18% { filter: brightness(1.5)
                drop-shadow(0 0 40px color-mix(in srgb, var(--rarity-core) 85%, transparent)); }
        100% { filter: brightness(1); }
      }
      /* the install punch (the committed pull). */
      .skillcard--install { animation: skillcardInstall 0.5s cubic-bezier(0.34,1.56,0.64,1); }
      @keyframes skillcardInstall {
        0% { filter: brightness(1); }
        30% { filter: brightness(1.35)
                drop-shadow(0 0 26px color-mix(in srgb, var(--rarity-core) 70%, transparent)); }
        100% { filter: brightness(1); }
      }

      @media (prefers-reduced-motion: reduce) {
        .skillcard { transition: opacity 1ms linear; transform: none;
          --tilt-x: 0deg; --tilt-y: 0deg; }
        .skillcard.hovered, .skillcard.selected { --lift-y: 0px; --lift-s: 1; }
        .skillcard-flip { transition: none; transform: none; }
        .skillcard--dealt .skillcard-flip,
        .skillcard--charging .skillcard-face--back,
        .skillcard--legendary-flare, .skillcard--install { animation: none; }
        .skillcard-seal-charge { transition: none; }
      }
    `;
    document.head.appendChild(style);
  }
}

/** A stable AXIOM-archive dossier number from the card id (render-only, pure). */
function dossierNo(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const n = (h % 22) + 1;
  return `${String(n).padStart(2, "0")}/22`;
}

/** Test/reset hook — drops the shared emblem bloom + the injected style guard. */
export function __resetSkillCardForTest(): void {
  SkillEmblem.resetSharedBloom();
  SkillCard["stylesInjected"] = false;
}
